import { type FC, forwardRef } from 'react';
import classNames from 'classnames/bind';
import { FAIcon, IconStyle } from '../Icons/Icons';
import Checkbox from '../Checkbox/Checkbox';
import { PromotionalMessageTypeTheme, PromotionalMessageType, PromotionalMessageTextSize } from './types/enums';
import { type PromotionalBannerPropsType } from './types/propTypes';
import styles from './PromotionalBanner.module.scss';

const cx = classNames.bind(styles);

const getIsCheckBoxEnabled = ({
  promotionType,
  checkbox,
}: {
  promotionType?: PromotionalMessageType;
  checkbox?: { disabled?: boolean; checked?: boolean };
}): boolean => !(promotionType === PromotionalMessageType.EXPIRED || checkbox?.disabled || !checkbox?.checked);

/**
 * @description PromotionalBanner Provides a banner component to display the Promotion ribbon with (fixed)Icon -  (dynamic)Text - (fixed)Icon.
 * Props ->
 * promotionType    : prop (String<Theme>) to differentiate between 2 type of ribbons Purple(POTENTIAL), Green(APPLIED)
 * promotionalText  : prop (String) for text to be displayed in the Promotional Ribbon.
 * onClick          : prop (Function) for on click handler.
 */
const PromotionalBanner: FC<PromotionalBannerPropsType> = forwardRef<HTMLDivElement, PromotionalBannerPropsType>(
  (
    {
      id = 'promotion-banner-id',
      iconName = 'tags',
      promotionType,
      promotionTitleText,
      promotionlDescription = '',
      promotionEndText = '',
      isExpanded = false,
      size = PromotionalMessageTextSize.SMALL,
      onClick,
      onClose,
      checkbox,
      hideChevronIcon = false,
    },
    ref,
  ) => {
    const isCheckBoxEnable = getIsCheckBoxEnabled({
      promotionType,
      checkbox: checkbox as { disabled?: boolean; checked?: boolean },
    });
    const showPromotionText =
      hideChevronIcon || isExpanded ? Boolean(promotionlDescription || promotionEndText) : false;

    const CClassname = cx('promotion-message', [PromotionalMessageTypeTheme[promotionType]], {
      expanded: showPromotionText,
      [PromotionalMessageTextSize.LARGE]: size === PromotionalMessageTextSize.LARGE,
      'promotion-active': isCheckBoxEnable && showPromotionText,
      'show-checkbox': checkbox?.id && showPromotionText,
      [`cursor-${onClick ? 'pointer' : 'default'}`]: true,
    });
    const CPMBClassname = cx('promotion-message-body', { 'with-description': showPromotionText });

    const showCloseButton = () =>
      onClose ? (
        <FAIcon
          iconName="close"
          iconStyle={IconStyle.SOLID}
          className={styles['close-icon']}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClose?.(id);
          }}
          data-testid="promotion-message-close-test-icon"
        />
      ) : null;

    return (
      <section
        id={id}
        className={CClassname}
        data-testid="promotion-message-test-id"
        role="presentation"
        ref={ref}
        onClick={(e) => {
          e.preventDefault();
          promotionType !== PromotionalMessageType.EXPIRED && onClick?.(id);
        }}
        onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      >
        {/* checkbox display */}
        <Checkbox
          text={''}
          {...checkbox}
          checked={isCheckBoxEnable}
          className={isCheckBoxEnable ? styles['isPromotionActive'] : ''}
        />
        {/* Main Content to be display */}
        <section
          className={styles['promotion-body']}
          role="combobox"
          tabIndex={0}
          aria-controls="promotion-body-id"
          aria-expanded={showPromotionText}
          data-testid="promotion-body-test-id"
        >
          <div className={styles['promotion-title']}>
            <div className={styles['promotion-message-main']}>
              <div className={styles['promotion-message-icon-left']}>
                <FAIcon
                  iconName={iconName}
                  iconStyle={IconStyle.SOLID}
                  className={styles['icon']}
                  data-testid="promotion-message-icon"
                />
              </div>
              <div className={CPMBClassname}>
                <div
                  className={showPromotionText ? '' : styles['ellipse']}
                  data-testid="promotion-message-body-test-id"
                >
                  {promotionTitleText}
                </div>
              </div>
            </div>
            {/** when isExpand prop is false then chevron icon should not be visible if hideChevronIcon is true, so added extra condition */}
            {!hideChevronIcon && !showPromotionText ? (
              <div className={styles['promotion-message-action']}>
                <FAIcon
                  iconName={'chevron-right'}
                  iconStyle={IconStyle.REGULAR}
                  className={styles['promotion-message-icon']}
                  data-testid="promotion-message-action-test-icon"
                />
              </div>
            ) : (
              showCloseButton()
            )}
          </div>
          {showPromotionText ? (
            <div className={styles['promotion-description']}>
              {promotionlDescription}
              {promotionEndText && <div className={styles['promotion-offer-end']}>{promotionEndText}</div>}
            </div>
          ) : null}
        </section>
      </section>
    );
  },
);
PromotionalBanner.displayName = 'PromotionalBanner';

export default PromotionalBanner;
export { PromotionalMessageType, PromotionalMessageTypeTheme, PromotionalMessageTextSize };
